// ═══════════════════════════════════════════════════════════════
// Impact Assessment Portal — Mobile-first field assessor view
// Route: /impactportal
// Auth: existing GAB login, no redirect — self-contained
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useAuthStore } from '../../../store/authStore';
import {
  IconMicrophone, IconList, IconClipboardList, IconLogout, IconChevronRight, IconChevronDown, IconCheck,
} from '@tabler/icons-react';
import '../../shelter/portal/ShelterPortal.css';
import './ImpactPortal.css';
import ImpactPortalAssess from './ImpactPortalAssess';
import ImpactPortalBulk from './ImpactPortalBulk';
import ImpactPortalReports from './ImpactPortalReports';

// ── Types ─────────────────────────────────────────────────────

type Tab = 'assess' | 'bulk' | 'reports';

const TABS: { id: Tab; label: string; Icon: (p: { size?: number }) => JSX.Element | null }[] = [
  { id: 'assess',  label: 'Assess',  Icon: IconMicrophone },
  { id: 'bulk',    label: 'Bulk',    Icon: IconList },
  { id: 'reports', label: 'Reports', Icon: IconClipboardList },
];

// ── Login screen ──────────────────────────────────────────────

function PortalLogin() {
  const { login } = useAuthStore();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('Email and password are required.'); return; }
    setLoading(true);
    setError('');
    try {
      await login(email.trim(), password);
    } catch (err: any) {
      setError(err?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sp-login-wrap">
      <div className="sp-login-card">
        <div className="sp-login-logo-row">
          <img
            src="/cds/assets/logo/OpenGov-Logo-Blurple.svg"
            alt="OpenGov"
            className="sp-login-og-logo"
          />
          <div className="sp-login-divider" />
          <span className="sp-login-product-name">Emergency Management</span>
        </div>
        <div className="sp-login-title">Impact Portal</div>
        <div className="sp-login-sub">Field assessor access — sign in to continue</div>

        <form onSubmit={submit}>
          {error && <div className="sp-error">{error}</div>}
          <div className="sp-field">
            <label className="sp-label">Email</label>
            <input
              className="sp-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              inputMode="email"
            />
          </div>
          <div className="sp-field">
            <label className="sp-label">Password</label>
            <input
              className="sp-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>
          <button type="submit" className="sp-btn sp-btn-primary" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Incident picker screen ────────────────────────────────────

function IncidentPicker() {
  const { incidents, activeIncident, setActiveIncident, loadIncidents, logout } = useAuthStore();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    loadIncidents().finally(() => setLoading(false));
  }, [loadIncidents]);

  return (
    <div className="sp-shell">
      <div className="sp-topbar">
        <img src="/cds/assets/logo/OpenGov-Logo-Blurple.svg" alt="OpenGov" className="sp-topbar-logo" />
        <div className="sp-topbar-divider" />
        <span className="sp-topbar-product">Impact Portal</span>
        <div style={{ flex: 1 }} />
        <button onClick={logout} className="sp-topbar-icon-btn" title="Sign out">
          <IconLogout size={16} />
        </button>
      </div>

      <div className="ip-incident-pick-wrap">
        <div className="ip-incident-pick-title">Select an Incident</div>
        <div className="ip-incident-pick-sub">Choose the incident you are responding to</div>

        {loading ? (
          <div className="ip-empty">
            <div className="ip-empty-sub">Loading incidents…</div>
          </div>
        ) : incidents.length === 0 ? (
          <div className="ip-empty">
            <div className="ip-empty-icon">⚠️</div>
            <div className="ip-empty-title">No incidents found</div>
            <div className="ip-empty-sub">Ask your supervisor to create an incident in the main app.</div>
          </div>
        ) : (
          <div className="ip-incident-pick-list">
            {incidents.map(inc => {
              const isActive = activeIncident?.id === inc.id;
              return (
                <button
                  key={inc.id}
                  className={`ip-incident-pick-row${isActive ? ' active' : ''}`}
                  onClick={() => setActiveIncident(inc)}
                >
                  <span className={`ip-incident-dot ip-incident-dot--${(inc as any).status || 'active'}`} />
                  <div className="ip-incident-pick-info">
                    <div className="ip-incident-pick-name">{inc.name}</div>
                    {(inc as any).status && (
                      <div className="ip-incident-pick-status">{(inc as any).status}</div>
                    )}
                  </div>
                  {isActive
                    ? <IconCheck size={18} style={{ color: '#4B3FFF', flexShrink: 0 }} />
                    : <IconChevronRight size={18} style={{ color: '#cbd5e1', flexShrink: 0 }} />
                  }
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main portal ───────────────────────────────────────────────

export default function ImpactPortal() {
  const { isAuthenticated, logout, activeIncident, incidents, setActiveIncident, loadIncidents } = useAuthStore();
  const [tab, setTab] = useState<Tab>('assess');
  const [showIncidentPicker, setShowIncidentPicker] = useState(false);

  useEffect(() => { if (isAuthenticated) loadIncidents(); }, [isAuthenticated]);

  if (!isAuthenticated) return <PortalLogin />;

  // Show full-page incident picker until one is selected
  if (!activeIncident) return <IncidentPicker />;

  return (
    <div className="sp-shell">
      {/* Top bar */}
      <div className="sp-topbar">
        <img
          src="/cds/assets/logo/OpenGov-Logo-Blurple.svg"
          alt="OpenGov"
          className="sp-topbar-logo"
        />
        <div className="sp-topbar-divider" />
        <span className="sp-topbar-product">Impact Portal</span>

        <button
          className="sp-shelter-btn"
          onClick={() => setShowIncidentPicker(v => !v)}
          title={activeIncident.name}
        >
          <span>{activeIncident.name}</span>
          <IconChevronDown size={12} />
        </button>

        <button onClick={logout} className="sp-topbar-icon-btn" title="Sign out">
          <IconLogout size={16} />
        </button>
      </div>

      {/* Incident picker dropdown */}
      {showIncidentPicker && (
        <div className="ip-incident-picker">
          <div className="ip-incident-picker-label">Select Incident</div>
          {incidents.map(inc => (
            <button
              key={inc.id}
              className={`ip-incident-option${activeIncident?.id === inc.id ? ' active' : ''}`}
              onClick={() => { setActiveIncident(inc); setShowIncidentPicker(false); }}
            >
              <span className={`ip-incident-dot ip-incident-dot--${(inc as any).status || 'active'}`} />
              <span className="ip-incident-name">{inc.name}</span>
              {activeIncident?.id === inc.id && <IconCheck size={14} style={{ color: '#4B3FFF', flexShrink: 0 }} />}
            </button>
          ))}
        </div>
      )}


      {/* Content */}
      <div className="sp-content" onClick={() => showIncidentPicker && setShowIncidentPicker(false)}>
        {tab === 'assess' ? (
          <ImpactPortalAssess onViewReports={() => setTab('reports')} />
        ) : tab === 'bulk' ? (
          <ImpactPortalBulk onViewReports={() => setTab('reports')} />
        ) : (
          <ImpactPortalReports />
        )}
      </div>

      {/* Bottom nav */}
      <nav className="sp-bottom-nav">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`sp-nav-btn${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <t.Icon size={20} />
            {t.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
